import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Link as LinkIcon, MessageSquare, XCircle } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

interface VincerBaseUser {
  name: string;
  email: string;
}

interface MentorData {
  uuid: string;
  vincer_base_user: VincerBaseUser;
}

interface MenteeData {
  uuid: string;
  vincer_base_user: VincerBaseUser;
}

interface MeetingData {
  uuid: string;
  scheduled_time: string;
  duration: number;
  status: 'scheduled' | 'completed' | 'cancelled';
  feedback?: any;
  meeting_link?: string;
  mentor_auth_token?: string;
  mentee_auth_token?: string;
  mentor: MentorData;
  mentee: MenteeData;
}

interface ScheduledMeeting {
  uuid: string;
  mentor: {
    name: string;
    email: string;
  };
  mentee: {
    name: string;
    email: string;
  };
  scheduled_time: string;
  duration: number;
  status: 'scheduled' | 'completed' | 'cancelled';
  feedback?: any;
  meeting_link?: string;
  mentor_auth_token?: string;
  mentee_auth_token?: string;
}

export function MeetingDetails() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState<ScheduledMeeting | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    async function fetchMeetingDetails() {
      try {
        const { data, error: fetchError } = await supabase
          .from('meeting')
          .select(`
            uuid,
            scheduled_time,
            duration,
            status,
            feedback,
            meeting_link,
            mentor_auth_token,
            mentee_auth_token,
            mentor:mentor_uuid (
              uuid,
              vincer_base_user (
                name,
                email
              )
            ),
            mentee:mentee_uuid (
              uuid,
              vincer_base_user (
                name,
                email
              )
            )
          `)
          .eq('uuid', uuid)
          .single();

        if (fetchError) throw fetchError;

        if (data) {
          const meetingData = data as unknown as MeetingData;
          setMeeting({
            uuid: meetingData.uuid,
            mentor: {
              name: meetingData.mentor.vincer_base_user.name,
              email: meetingData.mentor.vincer_base_user.email,
            },
            mentee: {
              name: meetingData.mentee.vincer_base_user.name,
              email: meetingData.mentee.vincer_base_user.email,
            },
            scheduled_time: meetingData.scheduled_time,
            duration: meetingData.duration,
            status: meetingData.status,
            feedback: meetingData.feedback,
            meeting_link: meetingData.meeting_link,
            mentor_auth_token: meetingData.mentor_auth_token,
            mentee_auth_token: meetingData.mentee_auth_token,
          });
        }
      } catch (err) {
        console.error('Error fetching meeting details:', err);
        setError('Failed to load meeting details');
      } finally {
        setLoading(false);
      }
    }

    if (uuid) {
      fetchMeetingDetails();
    }
  }, [uuid]);

  const formatDateTime = (dateTimeString: string) => {
    const date = new Date(dateTimeString);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleCancelMeeting = async () => {
    if (!meeting || meeting.status === 'cancelled') return;
    
    if (!confirm('Are you sure you want to cancel this meeting?')) return;

    setCancelling(true);
    try {
      const { error: updateError } = await supabase
        .from('meeting')
        .update({ status: 'cancelled' })
        .eq('uuid', meeting.uuid);

      if (updateError) throw updateError;

      // Update local state
      setMeeting({
        ...meeting,
        status: 'cancelled'
      });
    } catch (err) {
      console.error('Error cancelling meeting:', err);
      alert('Failed to cancel meeting');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffcc32] mx-auto mb-4"></div>
        <p className="text-gray-600">Loading meeting details...</p>
      </div>
    );
  }

  if (error || !meeting) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">{error || 'Meeting not found'}</p>
        <button
          onClick={() => navigate('/coordinator/meetings')}
          className="mt-4 text-blue-600 hover:underline"
        >
          Back to Meetings List
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <button
          onClick={() => navigate('/coordinator/meetings')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Meetings List
        </button>
        {meeting.status !== 'cancelled' && (
          <button
            onClick={handleCancelMeeting}
            disabled={cancelling}
            className="flex items-center gap-2 bg-red-50 text-red-700 px-4 py-2 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <XCircle className="w-5 h-5" />
            {cancelling ? 'Cancelling...' : 'Cancel Meeting'}
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold">Meeting Details</h1>
            <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(meeting.status)}`}>
              {meeting.status.charAt(0).toUpperCase() + meeting.status.slice(1)}
            </span>
          </div>

          <div className="flex items-center gap-3 text-gray-500 mb-2">
            <Calendar className="w-5 h-5" />
            <span>{formatDateTime(meeting.scheduled_time)}</span>
          </div>
          <div className="flex items-center gap-3 text-gray-500">
            <Clock className="w-5 h-5" />
            <span>{meeting.duration} minutes</span>
          </div>
        </div>

        <div className="p-6 border-b">
          <h2 className="font-semibold mb-4">Participants</h2>
          <div className="space-y-4">
            <div>
              <div className="font-medium">Mentor</div>
              <div className="text-gray-600">{meeting.mentor.name}</div>
              <div className="text-gray-500 text-sm">{meeting.mentor.email}</div>
            </div>
            <div>
              <div className="font-medium">Mentee</div>
              <div className="text-gray-600">{meeting.mentee.name}</div>
              <div className="text-gray-500 text-sm">{meeting.mentee.email}</div>
            </div>
          </div>
        </div>

        {meeting.meeting_link && (
          <div className="p-6 border-b">
            <h2 className="font-semibold mb-4 flex items-center gap-2">
              <LinkIcon className="w-5 h-5" />
              Meeting Link
            </h2>
            <a
              href={meeting.meeting_link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline break-all"
            >
              {meeting.meeting_link}
            </a>
          </div>
        )}

        {meeting.feedback && (
          <div className="p-6">
            <h2 className="font-semibold mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Feedback
            </h2>
            <pre className="whitespace-pre-wrap text-gray-600 font-mono text-sm bg-gray-50 p-4 rounded-lg">
              {JSON.stringify(meeting.feedback, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
} 